import React from 'react';
import { SendIcon, MailIcon, BriefcaseIcon, UserPlusIcon, ArrowRightIcon, MessageSquareIcon } from 'lucide-react';
import { useRegistry } from '../../contexts/RegistryContext';
import { formatWhen } from '../../utils/adminFormat';
export type AdminTab = 'overview' | 'registrations' | 'outreach' | 'messages' | 'jobs' | 'assignments' | 'activity';
interface Props {
  onNavigate: (tab: AdminTab) => void;
}
export function OverviewPanel({
  onNavigate
}: Props) {
  const {
    registrations,
    threads,
    jobs,
    assignments,
    activity
  } = useRegistry();
  const outreach = threads.filter(t => t.initiatedBy === 'owner');
  const inbound = threads.filter(t => t.initiatedBy !== 'owner');
  const unsentDrafts = outreach.filter(t => !t.dispatched);
  const unread = inbound.filter(t => t.unreadForOwner);
  const newRegistrations = registrations.filter(r => !r.reviewed);
  const openJobs = jobs.filter(j => j.status === 'open');
  const awaitingApplication = assignments.filter(a => a.status === 'invited');

  /* What genuinely needs the owner's attention, in the order it should be dealt with. */
  const attention = [unread.length && {
    key: 'unread',
    label: unread.length === 1 ? '1 message awaiting your reply' : `${unread.length} messages awaiting your reply`,
    detail: unread[0].contactName,
    tab: 'messages' as AdminTab
  }, unsentDrafts.length && {
    key: 'drafts',
    label: unsentDrafts.length === 1 ? '1 outreach draft not yet sent' : `${unsentDrafts.length} outreach drafts not yet sent`,
    detail: unsentDrafts[0].contactName,
    tab: 'outreach' as AdminTab
  }, newRegistrations.length && {
    key: 'registrations',
    label: newRegistrations.length === 1 ? '1 registration awaiting review' : `${newRegistrations.length} registrations awaiting review`,
    detail: newRegistrations[0].fullName,
    tab: 'registrations' as AdminTab
  }, awaitingApplication.length && {
    key: 'invited',
    label: awaitingApplication.length === 1 ? '1 invitation with no response yet' : `${awaitingApplication.length} invitations with no response yet`,
    detail: awaitingApplication[0].candidateName,
    tab: 'assignments' as AdminTab
  }].filter(Boolean) as {
    key: string;
    label: string;
    detail: string;
    tab: AdminTab;
  }[];
  const actions = [{
    key: 'outreach',
    tab: 'outreach' as AdminTab,
    Icon: SendIcon,
    title: 'Reach out to a candidate',
    body: 'Invite someone to register, introduce a mandate, or write to them directly.'
  }, {
    key: 'jobs',
    tab: 'jobs' as AdminTab,
    Icon: BriefcaseIcon,
    title: 'Create a job description',
    body: 'Write the brief once, then invite executives to apply to it.'
  }, {
    key: 'assignments',
    tab: 'assignments' as AdminTab,
    Icon: UserPlusIcon,
    title: 'Assign a job description',
    body: 'Invite a registered executive to apply, with a note on why it fits.'
  }, {
    key: 'messages',
    tab: 'messages' as AdminTab,
    Icon: MessageSquareIcon,
    title: 'Read your inbox',
    body: 'Conversations started by clients and candidates from the contact page.'
  }];
  return <section className="mt-8" data-id="element-311" data-mp="1yibe74szn8sl">
      {/* Needs attention — the reason to open this screen at all */}
      <div className="border-l-2 border-gold bg-white p-6" data-id="element-312" data-mp="1tioz0ptlnih5">
        <h2 className="font-serif text-[1.375rem] text-ink" data-id="element-313" data-mp="1tk9xurmy5nkt">Needs your attention</h2>
        {!attention.length ? <p className="mt-3 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-314" data-mp="1kpvfuhb17yy0">
            Nothing outstanding. Every message is answered, every registration reviewed, and no outreach is sitting
            unsent.
          </p> : <ul className="mt-5 divide-y divide-navy-100 border-y border-navy-100" data-id="element-315" data-mp="1ar5xknf84ch8">
            {attention.map(item => <li key={item.key} data-id="element-316" data-mp="15v75opuoscc2">
                <button type="button" onClick={() => onNavigate(item.tab)} className="group flex w-full items-center justify-between gap-4 py-3.5 text-left transition-colors duration-120 hover:bg-parchment" data-id="element-317" data-mp="35uu6k9f31rao">
                  <span className="min-w-0" data-id="element-318" data-mp="31444p65v86kq">
                    <span className="block font-sans text-[0.9375rem] text-ink" data-id="element-319" data-mp="312szd1wruqri">{item.label}</span>
                    <span className="mt-0.5 block truncate font-sans text-[0.8125rem] text-charcoal-500" data-id="element-320" data-mp="2n31eqibr2c4j">
                      Most recent · {item.detail}
                    </span>
                  </span>
                  <ArrowRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-charcoal-300 transition-transform duration-120 ease-institutional group-hover:translate-x-0.5 group-hover:text-ink" data-id="element-321" data-mp="2d7zt3wgons6p" />
                </button>
              </li>)}
          </ul>}
      </div>

      {/* Quick actions */}
      <h2 className="mt-10 font-sans text-micro uppercase tracking-[0.14em] text-charcoal-300" data-id="element-322" data-mp="20wyzsbjj0m0h">Start something</h2>
      <ul className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-323" data-mp="1vyp4m0xsxlev">
        {actions.map(({
        key,
        tab,
        Icon,
        title,
        body
      }) => <li key={key} data-id="element-324" data-mp="2ao3f5cd468vu">
            <button type="button" onClick={() => onNavigate(tab)} className="group flex h-full w-full flex-col border border-navy-100 bg-white p-5 text-left transition-colors duration-120 ease-institutional hover:border-navy-400" data-id="element-325" data-mp="25vk1kfeffdr1">
              <Icon aria-hidden="true" className="h-4 w-4 text-gold-700" data-id="element-326" data-mp="2tkutd7dstxt1" />
              <span className="mt-3 font-serif text-[1.0625rem] text-ink" data-id="element-327" data-mp="2tk36qm26jaew">{title}</span>
              <span className="mt-1.5 font-sans text-[0.8125rem] leading-relaxed text-charcoal-500" data-id="element-328" data-mp="2omb4oz7tm7wz">{body}</span>
              <span className="mt-auto pt-4 inline-flex items-center gap-1.5 font-sans text-[0.75rem] uppercase tracking-[0.1em] text-charcoal-300 group-hover:text-ink" data-id="element-329" data-mp="z73kia3anhyn">
                Open
                <ArrowRightIcon aria-hidden="true" className="h-3 w-3 transition-transform duration-120 ease-institutional group-hover:translate-x-0.5" data-id="element-330" data-mp="2ubun4brv1dbp" />
              </span>
            </button>
          </li>)}
      </ul>

      {/* Two live columns */}
      <div className="mt-10 grid gap-8 lg:grid-cols-2" data-id="element-331" data-mp="387oma32u0obm">
        <div data-id="element-332" data-mp="3d7t4pynavt4h">
          <div className="flex items-baseline justify-between gap-4" data-id="element-333" data-mp="3d82rldd1tk5y">
            <h2 className="font-serif text-[1.125rem] text-ink" data-id="element-334" data-mp="3i75pmdum2q4f">Latest registrations</h2>
            <button type="button" onClick={() => onNavigate('registrations')} className="font-sans text-[0.75rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-335" data-mp="3my5lrgvwp45v">
              View all
            </button>
          </div>
          {!registrations.length ? <p className="mt-3 border border-dashed border-navy-200 bg-white p-6 font-sans text-[0.8125rem] text-charcoal-500" data-id="element-336" data-mp="4tu8qx0bw7qz">
              No registrations yet.
            </p> : <ul className="mt-3 divide-y divide-navy-100 border-y border-navy-100 bg-white" data-id="element-337" data-mp="9x4tjlwodpi3">
              {registrations.slice(0, 5).map(r => <li key={r.id} className="px-4 py-3" data-id="element-338" data-mp="x7zf4rinyfn">
                  <p className="font-sans text-[0.875rem] text-ink" data-id="element-339" data-mp="yjc5u2xwgiw">
                    {r.fullName}
                    {r.reviewed ? null : <span className="ml-2 border border-gold bg-gold-100 px-1.5 py-0.5 font-sans text-[0.625rem] uppercase tracking-[0.1em] text-gold-700" data-id="element-340" data-mp="soe1hqqss67k">
                        New
                      </span>}
                  </p>
                  <p className="font-sans text-[0.8125rem] text-charcoal-500" data-id="element-341" data-mp="jvkk9nrsiwr2">
                    {r.professionalTitle || r.email}
                  </p>
                  <p className="mt-0.5 font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-342" data-mp="1bni7eqn1ljv0">
                    {formatWhen(r.submittedAt)}
                  </p>
                </li>)}
            </ul>}
        </div>

        <div data-id="element-343" data-mp="2043mwt3ums41">
          <div className="flex items-baseline justify-between gap-4" data-id="element-344" data-mp="203txp7qmooze">
            <h2 className="font-serif text-[1.125rem] text-ink" data-id="element-345" data-mp="1lenvdawhuina">Recent outreach</h2>
            <button type="button" onClick={() => onNavigate('outreach')} className="font-sans text-[0.75rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-346" data-mp="1qcz7xte6b797">
              View all
            </button>
          </div>
          {!outreach.length ? <button type="button" onClick={() => onNavigate('outreach')} className="mt-3 flex w-full items-center gap-2 border border-dashed border-navy-200 bg-white p-6 text-left font-sans text-[0.8125rem] text-charcoal-500 transition-colors duration-120 hover:border-navy-400" data-id="element-347" data-mp="243ch1z2sws04">
              <MailIcon aria-hidden="true" className="h-4 w-4 text-gold-700" data-id="element-348" data-mp="3bv19fxyb07ts" />
              You have not contacted anyone yet — write your first invitation.
            </button> : <ul className="mt-3 divide-y divide-navy-100 border-y border-navy-100 bg-white" data-id="element-349" data-mp="3kvzduhcnmhcw">
              {outreach.slice(0, 5).map(t => {
            const replied = t.messages.some(m => m.from === 'client');
            return <li key={t.id} className="px-4 py-3" data-id="element-350" data-mp="2t5l9pcug0jef">
                    <p className="font-sans text-[0.875rem] text-ink" data-id="element-351" data-mp="2o5ywh3em735m">{t.contactName}</p>
                    <p className="truncate font-sans text-[0.8125rem] text-charcoal-500" data-id="element-352" data-mp="31x3objbqqr5e">{t.subject}</p>
                    <p className="mt-0.5 font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-353" data-mp="2x6na3x03j1hz">
                      {replied ? 'Replied' : t.dispatched ? 'Sent · awaiting reply' : 'Draft · not sent'} ·{' '}
                      {formatWhen(t.updatedAt)}
                    </p>
                  </li>;
          })}
            </ul>}
        </div>
      </div>

      {/* Desk summary */}
      <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6 border-t border-navy-100 pt-6 md:grid-cols-4" data-id="element-354" data-mp="3pa93gy2dkklp">
        {[{
        label: 'Registered executives',
        value: registrations.length
      }, {
        label: 'People contacted',
        value: outreach.length
      }, {
        label: 'Open job descriptions',
        value: openJobs.length
      }, {
        label: 'Recorded events',
        value: activity.length
      }].map(s => <div key={s.label} data-id="element-355" data-mp="36h56b2py7pmc">
            <dt className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-356" data-mp="36gvcf41s8ybd">{s.label}</dt>
            <dd className="mt-1 font-serif text-[1.5rem] leading-none text-ink" data-id="element-357" data-mp="31hisar2aq5ch">{s.value}</dd>
          </div>)}
      </dl>
    </section>;
}